import type {
  CompetitionId,
  CompetitionView,
  EventId,
  EventTypeView,
  EventView,
  MarketId,
  MarketView,
  MeetingId,
  MeetingView,
  RaceId,
  RaceView
} from '@ps/isp-web-services-types';
import type { Selector } from 'react-redux';

import type {
  RootState,
  SelectorConfig,
  SelectorCreator,
  Slicer,
  WidgetModelConfig
} from '@swf/core/store';
import type { SportsWidgetConfig } from '@swf/sports/pages/types';
import type { EventTypeId } from '@swf/sports/types/eventType';

import type {
  BasicMarketTemplatesView,
  EventBasedMarketTemplatesView,
  MeetingBasedMarketTemplatesView
} from '../../types/templates';

interface MeetingAndEventBasedMarketTemplatesView extends BasicMarketTemplatesView {
  events: EventBasedMarketTemplatesView['events'];
  competitions: EventBasedMarketTemplatesView['competitions'];
  meetings: MeetingBasedMarketTemplatesView['meetings'];
  races: MeetingBasedMarketTemplatesView['races'];
}

export type EventTypesSelector = Selector<
  RootState,
  MeetingAndEventBasedMarketTemplatesView['eventTypes']
>;
export type MarketsSelector = Selector<
  RootState,
  MeetingAndEventBasedMarketTemplatesView['markets']
>;
export type CompetitionsSelector = Selector<
  RootState,
  MeetingAndEventBasedMarketTemplatesView['competitions']
>;
export type EventsSelector = Selector<RootState, MeetingAndEventBasedMarketTemplatesView['events']>;
export type MeetingsSelector = Selector<
  RootState,
  MeetingAndEventBasedMarketTemplatesView['meetings']
>;
export type RacesSelector = Selector<RootState, MeetingAndEventBasedMarketTemplatesView['races']>;

const initialState: MeetingAndEventBasedMarketTemplatesView = {
  competitions: {},
  events: {},
  eventTypes: {},
  meetings: {},
  races: {},
  markets: {}
};

export const selectors = (
  slice: Slicer<MeetingAndEventBasedMarketTemplatesView>,
  createSelector: SelectorCreator
) => ({
  eventTypes: (): EventTypesSelector => slice((state) => state.eventTypes),
  eventType(this: { eventTypes: EventTypesSelector }) {
    return createSelector(
      this.eventTypes,
      (_state: RootState, eventTypeId: EventTypeId) => eventTypeId,
      (eventTypes, eventTypeId): EventTypeView => eventTypes[eventTypeId] as EventTypeView
    );
  },
  competitions: (): CompetitionsSelector => slice((state) => state.competitions),
  competition(this: { competitions: CompetitionsSelector }) {
    return createSelector(
      this.competitions,
      (_state: RootState, competitionId: CompetitionId) => competitionId,
      (competitions, competitionId): CompetitionView => competitions[competitionId]
    );
  },
  events: (): EventsSelector => slice((state) => state.events),
  event(this: { events: EventsSelector }) {
    return createSelector(
      this.events,
      (_state: RootState, eventId: EventId) => eventId,
      (events, eventId): EventView => events[eventId]
    );
  },
  meetings: (): MeetingsSelector => slice((state) => state.meetings),
  meeting(this: { meetings: MeetingsSelector }) {
    return createSelector(
      this.meetings,
      (_state: RootState, meetingId: MeetingId) => meetingId,
      (meetings, meetingId): MeetingView => meetings[meetingId]
    );
  },
  races: (): RacesSelector => slice((state) => state.races),
  race(this: { races: RacesSelector }) {
    return createSelector(
      this.races,
      (_state: RootState, raceId: RaceId) => raceId,
      (races, raceId): RaceView => races[raceId]
    );
  },
  markets(this: { markets: MarketsSelector }) {
    return createSelector(
      slice,
      (_state: RootState, marketIds?: MarketId[]) => marketIds,
      ({ markets }, marketIds) => {
        if (!marketIds) {
          return markets;
        }

        return marketIds.reduce((acc, marketId) => {
          if (markets[marketId]) {
            return {
              ...acc,
              [marketId]: markets[marketId]
            };
          }

          return acc;
        }, {} as typeof markets);
      }
    );
  },
  market(this: { markets: MarketsSelector }) {
    return createSelector(
      (state: RootState) => this.markets(state),
      (_state: RootState, marketId: MarketId) => marketId,
      (markets, marketId): MarketView | undefined => markets[marketId]
    );
  },
  marketIds(this: { markets: MarketsSelector }) {
    return createSelector(this.markets, (markets) => Object.keys(markets));
  },
  marketTemplateData(this: {
    competitions: CompetitionsSelector;
    events: EventsSelector;
    eventTypes: EventTypesSelector;
    meetings: MeetingsSelector;
    races: RacesSelector;
    markets: MarketsSelector;
  }) {
    return createSelector(
      this.competitions,
      this.events,
      this.eventTypes,
      this.meetings,
      this.races,
      this.markets,
      (
        competitions,
        events,
        eventTypes,
        meetings,
        races,
        markets
      ): MeetingAndEventBasedMarketTemplatesView => ({
        competitions,
        events,
        eventTypes,
        meetings,
        races,
        markets
      })
    );
  }
});

export const withMeetingAndEventBasedMarketTemplateModelState = <T>(
  modelState: T
): T & MeetingAndEventBasedMarketTemplatesView => ({ ...initialState, ...modelState });

export function withMeetingAndEventBasedMarketTemplateModelSelectors<
  S extends MeetingAndEventBasedMarketTemplatesView,
  F extends (slice: Slicer<S>, createSelector: SelectorCreator) => SelectorConfig<S>
>(_state: S, modelSelectorFactory: F) {
  return (slice: Slicer<S>, createSelector: SelectorCreator) =>
    ({
      ...selectors(slice, createSelector),
      ...modelSelectorFactory(slice, createSelector)
    } as ReturnType<typeof selectors> & ReturnType<F>);
}

export interface MeetingAndEventBasedMarketTemplatesModel
  extends WidgetModelConfig<SportsWidgetConfig, MeetingAndEventBasedMarketTemplatesView> {
  selectors: typeof selectors;
}
